import {ISideMenuService, SideMenuLinkAction} from './side-menu.service.interface';
import {ServiceBase} from '../ServiceBase';
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {InternalLink, ExternalLink, SideMenuLinkConfiguration} from "../configurations/config-data-sections/side-menu.configuration";
import {action, observable} from "mobx";
import { isExternalLink, isInternalLink } from '../configurations/helpers/side-menu-typeguards.helpers';

export class SideMenuService extends ServiceBase implements ISideMenuService {
  @observable.ref isMobileMenuOpen: boolean = false;

  @action.bound
  private _setIsMobileMenuOpen(shouldOpen: boolean) {
    this.isMobileMenuOpen = shouldOpen;
  }

  constructor(serviceFactory: IServiceFactoryExtended) {
    super(serviceFactory);
    this.reaction(() => this.services.overlay.isOpen,
      (isOpen) => {
        if (!isOpen) {
          this.closeMobileMenu();
        }
      });
  }

  getLinkAction(config: SideMenuLinkConfiguration): SideMenuLinkAction {
    return this._linkAction(config);
  }

  openMobileMenu(): void {
    if (!this.isMobileMenuOpen) {
      this.services.userMenu.closeMenu();
      this.services.sdk.closeRunningGame();
      this.services.overlay.openOverlay();
      this._setIsMobileMenuOpen(true);
    }
  }

  closeMobileMenu(): void {
    if (this.isMobileMenuOpen) {
      this.services.overlay.closeOverlay();
      this.services.gameSearch.closeSearchResult();
      this.closeMobileLanguageSwitcher();

      this._setIsMobileMenuOpen(false);
    }
  }

  openHelp(): void {
    this.services.externalLinks.openHelpPage();
    this.services.userMenu.closeMenu();
    this.services.analytics.sendAnalytics('Navigation', 'Header', 'Help');
  }

  private _linkAction(config: SideMenuLinkConfiguration): SideMenuLinkAction {
    return event => {
      event.preventDefault();
      this.services.analytics.sendAnalytics('Navigation', 'NavBar', config.text);

      if (this.services.menuFlow.hasCustomFlow(config)) {
        const flowResult = this.services.menuFlow.executeCustomFlow(config);
        if (flowResult && !flowResult.continue) {
          return;
        }
      }

      this.closeMobileMenu();
      this.services.overlay.closeOverlay();
      this.services.userMenu.closeMenu();

      if (isExternalLink(config)) {
        this._externalLink(config);
      } else if (isInternalLink(config)) {
        this._internalLink(config);
      }
    };
  }

  private _internalLink(config: InternalLink) {
    const link = this.proceedLink(config.link);
    switch (config.tabState) {
      case '_blank':
        this.services.sdk.openInNewWindow(this.services.marketing.appendSCutToUrl(link));
        break;
      case '_self':
      default:
        this.services.location.push(link.toLowerCase());
        break;
    }
  }

  private _externalLink(config: ExternalLink) {
    const link = this.proceedLink(config.link);
    const linkParams = {
      source: ('externalLinkSource' in config) ? config.externalLinkSource : link,
      view: config.target,
      options: config.options,
      sourceLink: null,
    };
    if (config.useDirectLink) {
      if (config.noConcatScut) {
        linkParams.sourceLink = link;
      } else {
        linkParams.sourceLink = this.services.marketing.appendSCutToUrl(link);
      }
    }

    this.services.externalLinks.openLink(linkParams, {dl: config.dl}, config.useCallbackURL);
  }

  private proceedLink(link:string): string {
    //add all replaces for the link here
    return link?.replace('{cid}', this.services.user.cid);
  }

  @observable.ref
  isMobileLanguageSwitcher: boolean = false;

  @action.bound
  openMobileLanguageSwitcher(): void {
    this.isMobileLanguageSwitcher = true;
  }

  @action.bound
  closeMobileLanguageSwitcher(): void {
    this.isMobileLanguageSwitcher = false;
  }
}
